import { GridColDef } from "@mui/x-data-grid-pro";
import { Button, IconButton } from "@mui/material";

import dayjs from "dayjs";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import CustomItemStatus from "../../../../../../common/components/custom_item_status";

/**
 * GenerateColetaPrecosListaColumnDefinition 
 */

type Props = {
  onActionCopiar: (idColetaPreco: number) => void;
  onActionEditar: (idColetaPreco: number, openNewTab: boolean) => void;
  onActionDeletar: (idColetaPreco: number | null, openNewTab: boolean) => void;
  onActionExportar: (idColetaPreco: number) => void;
  onActionOrdemCompra: (idOrdemCompra: number, openNewTab: boolean) => void;
};

export const GenerateColetaPrecosListaColumnDefinition = ({
  onActionCopiar,
  onActionEditar,
  onActionDeletar,
  onActionExportar,
  onActionOrdemCompra,
}: Props): GridColDef[] => {
  const computeItemStatusTitle = (status: number) => {
    switch (status) {
      case 4:
        return "R";
      case 3:
        return "A";
      case 2:
        return "O";
      case 1:
        return "F";
      case 0:
        return "E";
      default:
        return "?";
    }
  };

  const computeItemStatusColor = (status: number) => {
    switch (status) {
      case 4:
        return "red";
      case 3:
        return "green";
      case 2:
        return "royalblue";
      case 1:
        return "yellow";
      case 0:
        return "gray";
      default:
        return "blue";
    }
  };

  const computeItemStatusDescription = (status: number) => {
    switch (status) {
      case 4:
        return "Coleta de preço reprovada";
      case 3:
        return "Coleta de preço aprovada";
      case 2:
        return "Coleta de preço gerou ordem de compra";
      case 1:
        return "Coleta de preço finalizada";
      case 0:
        return "Coleta de preço em elaboração";
      default:
        return "?";
    }
  };

  // Status Pagamento

  const computePagamentoStatusTitle = (status: number) => {
    switch (status) {
      case 1:
        return "NP";
      case 0:
        return "PP";
      default:
        return "?";
    }
  };

  const computePagamentoStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return "gray";
      case 0:
        return "green";
      default:
        return "blue";
    }
  };

  const computePagamentoStatusDescription = (status: number) => {
    switch (status) {
      case 1:
        return "Pagamento não provisionado";
      case 0:
        return "Pagamento provisionado";
      default:
        return "?";
    }
  };

  return [
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      minWidth: 55,
      width: 55,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <CustomItemStatus
            size="default"
            textSize="small"
            title={computeItemStatusTitle(value)}
            bgColor={computeItemStatusColor(value)}
            description={computeItemStatusDescription(value)} />
        </div>
      )
    },
    {
      field: "idPgtoColeta",
      filterable: false,
      headerName: "Status Pagamento",
      width: 80,
      minWidth: 80,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <CustomItemStatus
            size="autowidth"
            textSize="small"
            title={computePagamentoStatusTitle(value === null ? 1 : 0)}
            bgColor={computePagamentoStatusColor(value === null ? 1 : 0)}
            description={computePagamentoStatusDescription(value === null ? 1 : 0)} />
        </div>
      )
    },
    {
      field: "nrColetaPreco",
      filterable: false,
      headerName: "Nº",
      width: 60,
      minWidth: 60,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex items-center text-center text-xs py-2">
            {value}
          </div>
        );
      }
    },
    {
      field: "dataRef",
      filterable: false,
      headerName: "Data Coleta",
      minWidth: 80,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            {value && dayjs.utc(value).local().format('DD/MM/YYYY')}
          </div>
        );
      },
    },
    {
      field: "descricao",
      filterable: false,
      headerName: "Descrição",
      flex: 2,
      minWidth: 120,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            {value}
          </div>
        );
      }
    },
    {
      field: "centroCusto",
      filterable: false,
      headerName: "Centro de Custo",
      flex: 2,
      minWidth: 120,
      sortable: false,
      valueGetter: (_, row) => ([row['codigoCentroCusto'], row['descricaoCentroCusto']]),
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            {value[0]} - {value[1]}
          </div>
        );
      }
    },
    {
      field: "nrRequisicaoCompra",
      filterable: false,
      headerName: "Nº Requisição",
      minWidth: 60,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            {value}
          </div>
        );
      }
    },

    {
      field: "ordemCompra",
      filterable: false,
      headerName: "Ordem de Compra",
      sortable: false,
      minWidth: 130,
      valueGetter: (_, row) => {
        return [
          row['idOrdemCompra'],
          row['nrOrdemCompra'],
          row['ordemCompra'],
          row['cancelada'],
          row['entregue'],
        ];
      },
      renderCell: ({ value: [
        idOrdemCompra,
        nrOrdemCompra,
        ordemCompra,
        cancelada,
        entregue,
      ] }) => {
        const color = cancelada === true
          ? '#d84a38'
          : (entregue ? '#35aa47' : '#e6e6e6');

        return (
          nrOrdemCompra !== null &&
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            <Button
              onMouseDown={e => {
                if (e.button === 1) {
                  onActionOrdemCompra(idOrdemCompra, true);
                } else {
                  onActionOrdemCompra(idOrdemCompra, e.ctrlKey);
                }
                e.preventDefault();
              }}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 110,
                width: 110,
                fontSize: 11,
                paddingX: '.4rem',
                paddingY: 0,
                background: '#e6e6e6',
                borderLeft: `.3rem solid ${color}`,
                borderRadius: 0,
                color: 'black',
                fontWeight: 'normal',
              }}>
              {nrOrdemCompra} - {ordemCompra}
            </Button>
          </div>
        );
      },
    },
    {
      field: "nomeFantasia",
      filterable: false,
      headerName: "Fornecedor",
      flex: 3,
      minWidth: 200,
      sortable: false,
      renderCell: ({ value }) => {
        return (
          <div className="w-full h-full flex justify-center items-center text-center text-xs py-2">
            {value}
          </div>
        );
      }
    },
    {
      field: 'idColetaPreco',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      width: 190,
      resizable: false,
      valueGetter: (_, row) => ([row['idColetaPreco'], row['status'], row['statusRequisicao']]),
      renderCell: ({ value: [idColetaPreco, status, statusRequisicao] }) => {
        const disabled = statusRequisicao !== 1 && statusRequisicao !== 5;
        return (
          <div className="w-full h-full flex items-center justify-center gap-0">
            <IconButton
              disabled={disabled}
              onClick={() => { onActionCopiar(idColetaPreco) }}>
              <DynamicIcon
                nameIcon="ContentCopy"
                size={20}
                className={disabled ? 'text-gray-500' : 'text-purple-500'}
              />
            </IconButton>

            <IconButton
              onMouseDown={e => {
                if (e.button === 1) {
                  onActionEditar(idColetaPreco, true);
                } else {
                  onActionEditar(idColetaPreco, e.ctrlKey);
                }
                e.preventDefault();
              }}>
              <DynamicIcon
                nameIcon="EditRounded"
                size={20}
                className="dark:text-white text-primary-green"
              />
            </IconButton>

            <IconButton
              onClick={() => { onActionDeletar(idColetaPreco, true) }}
              disabled={status === 2}>
              <DynamicIcon
                nameIcon="DeleteForeverRounded"
                size={20}
                className={status !== 2 ? 'text-red-500' : 'text-gray-500'}
              />
            </IconButton>

            <Button
              onMouseDown={() => { onActionExportar(idColetaPreco) }}
              sx={{
                fontSize: 9,
                paddingX: '.4rem',
                paddingY: 0,
                background: '#e6e6e6',
                borderLeft: '.2rem solid #45b6af',
                borderRadius: 0,
                color: 'black',
                fontWeight: 'normal',
              }}>
              Exportar
            </Button>
          </div>
        );
      }
    },
  ];
}